// Shared primitives for CallModal + IncomingCallScreen.
// Pure presentational — no SDK or Redux. Lifted from the design bundle
// (Claude Design/TransferPitch Call Modal/modal.jsx) and pinned to the
// same color tokens so the two surfaces (active call + incoming) look
// identical.

import React from "react";

// — Design tokens ————————————————————————————————————————————————————————
// Tokens for the LIGHT call modal — matches the rest of the TransferPitch
// app (white cards, navy text, TP blue accents). The ONLY surface that
// stays dark is the inner video plate, which is set inline in CallModal
// because video reads best on dark (every other video app does this —
// Meet, Zoom, Teams).
//
//   - blue       — same #2283FF used on StandingsWidget topbar, primary
//                  buttons, the floating support icon, etc.
//   - text       — navy used everywhere else in the app (e.g. modal titles)
//   - modal/surface — light grey panels for the modal shell + slide-ins
//   - border     — same #E5E7EB used across the app's card outlines
export const TP = {
  blue: "#2283FF",
  blueDeep: "#1968D5",
  bgDeep: "#F6F8FB",
  modal: "#FFFFFF",
  surface: "#F8FAFC",
  surface2: "#F1F5F9",
  border: "#E5E7EB",
  borderStrong: "#CBD5E1",
  text: "#1A212F",
  text2: "#6B7280",
  text3: "#9CA3AF",
  ok: "#10B981",
  danger: "#EF4444",
  warn: "#F59E0B",
};

// — Icon shell ——————————————————————————————————————————————————————————
// `d` may be either a string (single <path d=...>) or a React fragment
// containing multiple <path>/<rect>/<line> children. This matches how the
// design bundle declares its icon set.
export const Icon = ({ d, size = 20, stroke = 2, fill }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={fill || "none"}
    stroke="currentColor"
    strokeWidth={stroke}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0 }}
  >
    {typeof d === "string" ? <path d={d} /> : d}
  </svg>
);

// — Icon set (24px line icons, currentColor) ———————————————————————————————
export const I = {
  mic: (
    <>
      <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <path d="M12 19v3" />
    </>
  ),
  micOff: (
    <>
      <path d="m2 2 20 20" />
      <path d="M18.89 13.23A7 7 0 0 0 19 12v-2" />
      <path d="M5 10v2a7 7 0 0 0 12 5" />
      <path d="M15 9.34V5a3 3 0 0 0-5.68-1.33" />
      <path d="M9 9v3a3 3 0 0 0 5.12 2.12" />
      <path d="M12 19v3" />
    </>
  ),
  cam: (
    <>
      <rect x="2" y="6" width="14" height="12" rx="2" />
      <path d="m22 8-6 4 6 4V8Z" />
    </>
  ),
  camOff: (
    <>
      <path d="M16 16v2a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h2" />
      <path d="M22 8 16 12l6 4V8Z" />
      <path d="M10 6h4a2 2 0 0 1 2 2v3" />
      <path d="m2 2 20 20" />
    </>
  ),
  speaker: (
    <>
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
      <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
      <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
    </>
  ),
  speakerOff: (
    <>
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
      <line x1="22" y1="9" x2="16" y2="15" />
      <line x1="16" y1="9" x2="22" y2="15" />
    </>
  ),
  phone: (
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  ),
  phoneOff: (
    <>
      <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.42 19.42 0 0 1-3.33-2.67" />
      <path d="M5.09 8.85a16.18 16.18 0 0 1-2.62-4.59A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91" />
      <line x1="2" y1="2" x2="22" y2="22" />
    </>
  ),
  rec: <circle cx="12" cy="12" r="6" />,
  share: (
    <>
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
      <path d="m9 10 3-3 3 3" />
      <line x1="12" y1="7" x2="12" y2="14" />
    </>
  ),
  minimize: (
    <>
      <polyline points="4 14 10 14 10 20" />
      <polyline points="20 10 14 10 14 4" />
      <line x1="14" y1="10" x2="21" y2="3" />
      <line x1="3" y1="21" x2="10" y2="14" />
    </>
  ),
  close: (
    <>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </>
  ),
  shieldOk: (
    <>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <polyline points="9 12 11 14 15 10" />
    </>
  ),
  gear: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </>
  ),
  chevDown: <polyline points="6 9 12 15 18 9" />,
  chevUp: <polyline points="18 15 12 9 6 15" />,
  check: <polyline points="20 6 9 17 4 12" />,
  // Background-blur indicator: three concentric circles suggesting depth/blur.
  // Lucide-style "sparkle-circle" — works in active (blue) + idle (neutral) states.
  blur: (
    <>
      <circle cx="12" cy="12" r="9" opacity="0.45" />
      <circle cx="12" cy="12" r="5.5" opacity="0.7" />
      <circle cx="12" cy="12" r="2" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </>
  ),
  plus: (
    <>
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </>
  ),
  addUser: (
    <>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <line x1="19" y1="8" x2="19" y2="14" />
      <line x1="22" y1="11" x2="16" y2="11" />
    </>
  ),
  expand: (
    <>
      <polyline points="15 3 21 3 21 9" />
      <polyline points="9 21 3 21 3 15" />
      <line x1="21" y1="3" x2="14" y2="10" />
      <line x1="3" y1="21" x2="10" y2="14" />
    </>
  ),
};

// — Avatar (initials over deterministic brand gradient) ——————————————————
export function Avatar({ name, size = 56, ring, pulse, online }) {
  const safe = (name || "?").trim();
  const initials =
    safe
      .split(/\s+/)
      .map((w) => w[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?";
  const h = [...safe].reduce((a, c) => a + c.charCodeAt(0), 0) % 360;
  return (
    <div
      style={{
        position: "relative",
        width: size,
        height: size,
        flexShrink: 0,
      }}
    >
      {pulse && (
        <>
          <span className="tp-pulse" style={{ animationDelay: "0s" }} />
          <span className="tp-pulse" style={{ animationDelay: ".8s" }} />
        </>
      )}
      <div
        style={{
          width: "100%",
          height: "100%",
          borderRadius: "50%",
          background: `linear-gradient(135deg, hsl(${h} 65% 45%), hsl(${
            (h + 40) % 360
          } 55% 30%))`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          fontWeight: 600,
          fontSize: size * 0.36,
          letterSpacing: -0.5,
          boxShadow: ring
            ? `0 0 0 3px ${TP.modal}, 0 0 0 5px ${TP.blue}`
            : "none",
          position: "relative",
          zIndex: 1,
        }}
      >
        {initials}
      </div>
      {online && (
        <span
          style={{
            position: "absolute",
            right: 0,
            bottom: 0,
            width: size * 0.22,
            height: size * 0.22,
            borderRadius: "50%",
            background: TP.ok,
            border: `2px solid ${TP.modal}`,
            zIndex: 2,
          }}
        />
      )}
    </div>
  );
}

// — Connection bars (signal strength) ——————————————————————————————————
export function Bars({ level = 3 }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-end",
        gap: 2,
        height: 14,
      }}
    >
      {[4, 7, 10, 13].map((h, i) => (
        <div
          key={i}
          style={{
            width: 3,
            height: h,
            borderRadius: 1,
            background: i < level ? TP.ok : "rgba(255,255,255,0.18)",
          }}
        />
      ))}
    </div>
  );
}

// — Encrypted chip ———————————————————————————————————————————————————————
export const SecureChip = () => (
  <div
    style={{
      marginTop: 8,
      padding: "5px 11px",
      borderRadius: 999,
      background: "rgba(16,185,129,0.12)",
      border: "1px solid rgba(16,185,129,0.3)",
      color: TP.ok,
      fontSize: 11,
      fontWeight: 600,
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      letterSpacing: 0.3,
    }}
  >
    <Icon d={I.shieldOk} size={12} stroke={2.5} /> ENCRYPTED · TransferPitch
  </div>
);

// — Audio waveform visualizer ——————————————————————————————————————————
export function Waveform({ bars = 56, speaking = true }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 3,
        height: 80,
        justifyContent: "center",
      }}
    >
      {Array.from({ length: bars }).map((_, i) => {
        const delay = (i * 0.04).toFixed(2);
        return (
          <span
            key={i}
            className={speaking ? "tp-wave" : ""}
            style={{
              display: "inline-block",
              width: 3,
              borderRadius: 2,
              background: `linear-gradient(180deg, ${TP.blue}, #6BA9E8)`,
              height: 8,
              animationDelay: `${delay}s`,
            }}
          />
        );
      })}
    </div>
  );
}

// — Live mm:ss timer ————————————————————————————————————————————————————
export function Timer({ startedAt }) {
  const [, force] = React.useState(0);
  React.useEffect(() => {
    const id = setInterval(() => force((v) => v + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const elapsed = startedAt
    ? Math.max(0, Math.floor((Date.now() - startedAt) / 1000))
    : 0;
  const m = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const s = String(elapsed % 60).padStart(2, "0");
  return (
    <span
      style={{
        fontVariantNumeric: "tabular-nums",
        fontSize: 13,
        color: TP.text,
      }}
    >
      {m}:{s}
    </span>
  );
}

// — Chrome titlebar (TP logo + title/subtitle + minimize/close) ————————————
//
// `chromeBtn` is the variant used inside dark overlay panels (slide-in
// settings / add-participant) — translucent against a dark backdrop.
// `chromeHeaderBtn` is the variant used inside the new TP-blue header
// strip — translucent against TP blue, white icons.
export const chromeBtn = {
  background: "rgba(255,255,255,0.04)",
  border: `1px solid ${TP.border}`,
  color: TP.text2,
  width: 28,
  height: 28,
  borderRadius: 8,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
};

export const chromeHeaderBtn = {
  background: "rgba(255,255,255,0.18)",
  border: "1px solid rgba(255,255,255,0.22)",
  color: "#fff",
  width: 28,
  height: 28,
  borderRadius: 8,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
};

export function Chrome({ title, subtitle, onMinimize, onClose, accent, transparent }) {
  // The header is now solid TransferPitch blue (same #2283FF used on the
  // home StandingsWidget topbar and the IncomingCallScreen header strip)
  // so the call modal reads as part of the same family of dialogs. When
  // overlaid on a video the strip stays opaque rather than transparent —
  // the brand sliver is the visual hook back to "this is TransferPitch."
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "12px 16px",
        background: TP.blue,
        position: transparent ? "absolute" : "relative",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 5,
        color: "#fff",
      }}
    >
      <div
        style={{
          width: 26,
          height: 26,
          borderRadius: 6,
          background: "rgba(255,255,255,0.18)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          fontWeight: 700,
          fontSize: 12,
          letterSpacing: -0.5,
        }}
      >
        TP
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          {title}
          {accent && (
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: accent,
              }}
            />
          )}
        </div>
        {subtitle && (
          <div style={{ fontSize: 11, opacity: 0.85, marginTop: 1 }}>
            {subtitle}
          </div>
        )}
      </div>
      {onMinimize && (
        <button onClick={onMinimize} aria-label="Minimize" title="Minimize call" style={chromeHeaderBtn}>
          <Icon d={I.minimize} size={14} />
        </button>
      )}
      {onClose && (
        <button onClick={onClose} aria-label="Close" title="End call" style={chromeHeaderBtn}>
          <Icon d={I.close} size={14} />
        </button>
      )}
    </div>
  );
}

// — Split control button: icon left + optional chevron right ————————————
// The chevron is only rendered when `withChevron` is true AND `onChevron`
// is provided. The chevron opens a dropdown (rendered by the parent via
// the `dropdown` slot). We keep the dropdown positioning here so callers
// don't need their own `position: relative` wrapper.
export function CtrlButton({
  icon,
  label,
  active,
  danger,
  ok,
  mute,
  onClick,
  withChevron = false,
  onChevron,
  chevOpen = false,
  dropdown = null,
}) {
  // Palette tuned for the LIGHT modal surround: neutral buttons are
  // white with a subtle border and dark icons (visible on the light
  // grey #F6F8FB shell). Danger / ok / active stay strong-coloured so
  // they read at a glance.
  const palette = danger
    ? { bg: TP.danger, fg: "#fff", border: TP.danger }
    : ok
    ? { bg: TP.ok, fg: "#fff", border: TP.ok }
    : active
    ? { bg: TP.blue, fg: "#fff", border: TP.blue }
    : mute
    ? {
        bg: "#FEE2E2",
        fg: "#B91C1C",
        border: "#FCA5A5",
      }
    : {
        bg: "#FFFFFF",
        fg: "#1A212F",
        border: "#E5E7EB",
      };

  const hasChevron = withChevron && typeof onChevron === "function";

  return (
    <div style={{ position: "relative", display: "inline-flex" }}>
      <div
        className="tp-ctrl"
        style={{
          display: "inline-flex",
          borderRadius: 12,
          overflow: "hidden",
          background: palette.bg,
          border: `1px solid ${palette.border}`,
        }}
      >
        <button
          onClick={onClick}
          aria-label={label}
          title={label}
          style={{
            background: "transparent",
            color: palette.fg,
            border: "none",
            padding: hasChevron ? "12px 10px 12px 14px" : "12px 14px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Icon d={icon} size={20} stroke={2} />
        </button>
        {hasChevron && (
          <button
            onClick={onChevron}
            aria-label={`${label} source options`}
            title={`${label} options`}
            aria-haspopup="menu"
            aria-expanded={chevOpen}
            style={{
              background: "transparent",
              color: palette.fg,
              opacity: 0.75,
              border: "none",
              borderLeft: `1px solid ${
                // White divider on coloured bg (danger/ok/active),
                // grey divider on the neutral white button.
                danger || ok || active
                  ? "rgba(255,255,255,0.35)"
                  : "#E5E7EB"
              }`,
              padding: "0 8px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Icon d={chevOpen ? I.chevUp : I.chevDown} size={14} stroke={2.5} />
          </button>
        )}
      </div>
      {chevOpen && dropdown}
    </div>
  );
}

// — Source-picker dropdown (above the control bar) ——————————————————————
// Renders the list of devices returned by useMediaDevices. The active
// device gets a check mark; clicking any row calls `onPick(deviceId)`
// and closes the menu via `onClose`. The `levels` prop is a Map of
// deviceId → number-in-[0,1] for the mic input meter (only the active
// mic's level is read because we can't tap inactive mics without taking
// over their stream).
export function SourceMenu({
  title,
  items,
  activeId,
  onPick,
  onClose,
  level = 0,
  showLevels = false,
  emptyHint = "No devices available",
}) {
  const menuRef = React.useRef(null);

  // Close on outside click. Pointerdown rather than click so we don't
  // race with the chevron that opened us.
  React.useEffect(() => {
    function handlePointerDown(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose?.();
      }
    }
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [onClose]);

  return (
    <div
      ref={menuRef}
      role="menu"
      className="tp-source-menu"
      style={{
        position: "absolute",
        bottom: "calc(100% + 10px)",
        left: "50%",
        transform: "translateX(-50%)",
        width: 280,
        background: "#FFFFFF",
        border: `1px solid ${TP.border}`,
        borderRadius: 14,
        boxShadow: "0 16px 40px rgba(20,40,80,0.18), 0 0 0 1px rgba(20,40,80,0.04)",
        overflow: "hidden",
        zIndex: 30,
      }}
    >
      <div
        style={{
          padding: "12px 14px",
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: 0.6,
          textTransform: "uppercase",
          color: TP.text2,
          borderBottom: `1px solid ${TP.border}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span>{title}</span>
        <button
          onClick={onClose}
          aria-label="Close menu"
          style={{
            background: "none",
            border: "none",
            color: TP.text2,
            cursor: "pointer",
            padding: 2,
            display: "flex",
          }}
        >
          <Icon d={I.close} size={14} />
        </button>
      </div>
      <div style={{ padding: 6, maxHeight: 280, overflowY: "auto" }}>
        {items.length === 0 ? (
          <div
            style={{
              padding: "14px 12px",
              fontSize: 12,
              color: TP.text3,
              textAlign: "center",
            }}
          >
            {emptyHint}
          </div>
        ) : (
          items.map((it) => {
            const isActive = it.deviceId === activeId;
            return (
              <button
                key={it.deviceId || it.label}
                role="menuitemradio"
                aria-checked={isActive}
                onClick={() => {
                  onPick(it.deviceId);
                  onClose?.();
                }}
                style={{
                  width: "100%",
                  background: isActive ? "#EBF3FF" : "transparent",
                  border: "none",
                  color: TP.text,
                  padding: "10px 10px",
                  borderRadius: 8,
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  cursor: "pointer",
                  textAlign: "left",
                  fontSize: 13,
                  fontWeight: 500,
                }}
              >
                <span
                  style={{ width: 16, color: TP.blue, display: "flex", flexShrink: 0 }}
                >
                  {isActive && <Icon d={I.check} size={16} stroke={2.5} />}
                </span>
                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {it.label || "Unnamed device"}
                </span>
                {showLevels && isActive && (
                  <MicLevelBars level={level} />
                )}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

// — Mic input level visualizer (5 bars, fills based on RMS) ————————————
// Used inside SourceMenu next to the active mic row. The same component
// could be reused in a future Settings-Panel audio tab.
export function MicLevelBars({ level = 0 }) {
  return (
    <span
      aria-hidden="true"
      style={{ display: "inline-flex", gap: 2, flexShrink: 0 }}
    >
      {[0.15, 0.32, 0.5, 0.7, 0.88].map((thresh, i) => {
        const lit = level >= thresh;
        // Color: green up to 0.7, then amber as a peak warning.
        const color = lit
          ? thresh >= 0.7
            ? TP.warn
            : TP.ok
          : "rgba(255,255,255,0.12)";
        return (
          <span
            key={i}
            style={{
              width: 2,
              height: 10,
              borderRadius: 1,
              background: color,
            }}
          />
        );
      })}
    </span>
  );
}
